export const GenerationTargetType = {
  STORYBOARD: 1,
  CHARACTER: 2,
  SCENE: 3,
  PROP: 4,
  RESOURCE: 5,
  SOUND_EFFECT: 6,
  VOICE_OVER: 7,
} as const;

export type GenerationTargetTypeValue =
  (typeof GenerationTargetType)[keyof typeof GenerationTargetType];

export const GenerationMediaType = {
  IMAGE: 1,
  VIDEO: 2,
  AUDIO: 3,
  VOICE: 4,
  TEXT: 5,
} as const;

export type GenerationMediaTypeValue =
  (typeof GenerationMediaType)[keyof typeof GenerationMediaType];

export const GenerationResourceType = {
  PROJECT_RESOURCE: 1,
  STORYBOARD_IMAGE: 2,
  STORYBOARD_VIDEO: 3,
  ATTACHMENT: 4,
  SOUND_EFFECT: 5,
  VOICE_OVER: 6,
  HUMAN_ASSET: 7,
} as const;

export type GenerationResourceTypeValue =
  (typeof GenerationResourceType)[keyof typeof GenerationResourceType];

export const GenerationReferenceRole = {
  REFERENCE_IMAGE: 'reference_image',
  REFERENCE_VIDEO: 'reference_video',
  REFERENCE_AUDIO: 'reference_audio',
  FIRST_FRAME: 'first_frame',
  LAST_FRAME: 'last_frame',
  VIDEO_EXTEND_SOURCE: 'video_extend_source',
} as const;

export type GenerationReferenceRoleValue =
  (typeof GenerationReferenceRole)[keyof typeof GenerationReferenceRole];

export const GenerationProvenanceSource = {
  DRAFT: 'draft',
  REQUEST_COMPAT: 'request_compat',
  BACKFILL: 'backfill',
} as const;

export type GenerationProvenanceSourceValue =
  (typeof GenerationProvenanceSource)[keyof typeof GenerationProvenanceSource];

export interface GenerationTargetKey {
  projectId: string;
  targetType: GenerationTargetTypeValue;
  targetId: string;
  tagId?: string | null;
  planId?: string | null;
  mediaType: GenerationMediaTypeValue;
}

export interface GenerationResourceSnapshot {
  name: string;
  url: string;
  thumbnailUrl?: string | null;
  mimeType?: string | null;
  checksum?: string | null;
  /** 仅与 url 对应的源文件大小。 */
  fileSize?: number | null;
  width?: number | null;
  height?: number | null;
  durationSeconds?: number | null;
}

export interface GenerationReferenceItem {
  resourceType: GenerationResourceTypeValue;
  resourceId: string;
  role: GenerationReferenceRoleValue;
  ordinal: number;
  snapshot: GenerationResourceSnapshot;
}

export interface GenerationProvenanceV1 {
  version: 1;
  references: GenerationReferenceItem[];
  frozenAt: string;
  draftRevision?: number | null;
  source: GenerationProvenanceSourceValue;
}

/**
 * 引用素材的人像认证状态，由服务端读取草稿时实时解析。
 * 不并进 `GenerationReferenceItem`：item 会被原样回传给 add 接口，而后端契约禁止多余字段。
 */
export interface GenerationReferenceHumanAsset {
  resourceType: GenerationResourceTypeValue;
  resourceId: string;
  humanAssetType?: number | null;
  humanAssetStatus?: number | null;
}

export interface GenerationReferenceDraft {
  target: GenerationTargetKey;
  references: GenerationReferenceItem[];
  humanAssets?: GenerationReferenceHumanAsset[];
  updatedBy?: string | null;
  updatedAt?: string | null;
}

export interface GenerationReferenceAddOperation {
  operation: 'add';
  reference: GenerationReferenceItem;
}

export interface GenerationReferenceRemoveOperation {
  operation: 'remove';
  resourceType: GenerationResourceTypeValue;
  resourceId: string;
  role: GenerationReferenceRoleValue;
}

export type GenerationReferenceDraftOperation =
  | GenerationReferenceAddOperation
  | GenerationReferenceRemoveOperation;
